import { signal, WritableSignal } from '@angular/core';
import { GeoJSON } from 'geojson';
import { GeoJSONSource as MapLibreGeoJSONSource, LayerSpecification, LngLatBounds, Map } from 'maplibre-gl';

import { MapBaseSource } from '@connect-services/client/components/map/map.component';
import { MapboxStyle } from '@connect-services/src/sdk/api';

export class MapBaseService {
    public static readonly defaultZoom = 5;
    public static readonly isFitBoundNeeded: WritableSignal<boolean> = signal<boolean>(true);
    public static readonly isMapReady = signal<boolean>(false);
    public static map?: Map;

    protected static mapBounds?: LngLatBounds;

    public get map(): Map | undefined {
        return MapBaseService.map;
    }

    public get mapBounds(): LngLatBounds | undefined {
        return MapBaseService.mapBounds;
    }

    protected set map(map: Map | undefined) {
        MapBaseService.map = map;
    }

    protected set mapBounds(mapBounds: LngLatBounds | undefined) {
        MapBaseService.mapBounds = mapBounds;
    }

    public createLayerId(sourceId: string, styleId: string): string {
        return `${sourceId}-${styleId}`;
    }

    public getMapBoundsCorners(): number[][] {
        const bounds: LngLatBounds = this.map!.getBounds();
        const northEast: [number, number] = [bounds.getNorthEast().lng, bounds.getNorthEast().lat];
        const northWest: [number, number] = [bounds.getNorthWest().lng, bounds.getNorthWest().lat];
        const southWest: [number, number] = [bounds.getSouthWest().lng, bounds.getSouthWest().lat];
        const southEast: [number, number] = [bounds.getSouthEast().lng, bounds.getSouthEast().lat];

        return [northEast, northWest, southWest, southEast, northEast];
    }

    public getMapZoom(): number {
        return Math.round(this.map?.getZoom() ?? MapBaseService.defaultZoom);
    }

    public isMapPresent(): this is this & { map: Map } {
        return !!this.map && !this.map._removed;
    }

    public isZoomIncluded(maxzoom: number | null | undefined, minzoom: number | null | undefined): boolean {
        if (!maxzoom || !minzoom) {
            return true;
        }

        return this.getMapZoom() >= minzoom && this.getMapZoom() <= maxzoom;
    }

    /**
     * Draw or move an invisible layer corresponding to the sourceId in order to make it easier to order layers
     * @param sourceId - The source id
     * @param beforeId - The id that will be displayed above, if any
     * @protected
     */
    protected addBaseSourceLayer(sourceId: string, beforeId?: string): void {
        if (!this.isMapPresent()) {
            return;
        }

        if (!this.map.getLayer(sourceId)) {
            this.map.addLayer(
                {
                    id: sourceId,
                    render: () => {},
                    type: 'custom',
                },
                beforeId,
            );
        } else {
            this.map.moveLayer(sourceId, beforeId);
        }
    }

    protected addGeoJSONSource(sourceId: string, geojson: GeoJSON | string): void {
        const mapSource = this.map?.getSource(sourceId) as MapLibreGeoJSONSource | undefined;
        if (mapSource) {
            mapSource.setData(geojson);

            return;
        }

        this.map?.addSource(sourceId, {
            data: geojson,
            lineMetrics: true,
            type: 'geojson',
        });
    }

    protected addSourceStyle(sourceId: string, style: MapboxStyle[], beforeId?: string): void {
        if (!this.isMapPresent()) {
            return;
        }

        style.forEach((style: MapboxStyle): void => {
            const layer = {
                ...style,
                id: this.createLayerId(sourceId, style.id),
                source: sourceId, // reference to the data source- one source can have multiple layers
            };
            if (!this.map.getLayer(layer.id)) {
                this.map.addLayer(layer as LayerSpecification, beforeId);
            } else {
                this.map.moveLayer(layer.id, beforeId);
            }
        });
    }

    protected getAdjustedCenterZoom(minZoom?: number, maxZoom?: number): number {
        const mapZoom = this.map!.getZoom();

        if (minZoom === undefined && maxZoom === undefined) {
            return mapZoom;
        }

        if (maxZoom === undefined && minZoom !== undefined && mapZoom < minZoom) {
            return minZoom;
        }

        if (maxZoom !== undefined && minZoom === undefined && mapZoom > maxZoom) {
            return maxZoom;
        }

        if (mapZoom < minZoom! || mapZoom > maxZoom!) {
            const averageZoom = (minZoom! + maxZoom!) / 2;

            return Math.round(averageZoom);
        }

        return mapZoom;
    }

    protected setSourceVisibility(sourceId: string, style: MapboxStyle[], isVisible: boolean): void {
        if (!this.isMapPresent()) {
            return;
        }

        style.forEach((style: MapboxStyle): void => {
            const layerId = this.createLayerId(sourceId, style.id);
            this.map.setLayoutProperty(layerId, 'visibility', isVisible ? 'visible' : 'none');
        });
    }

    protected toggleSourceVisibility(source: MapBaseSource<unknown>): void {
        source.style.forEach((style: MapboxStyle) => {
            if (!this.isMapPresent()) {
                return;
            }

            const layerId = this.createLayerId(source.sourceId, style.id);

            if (this.map.getLayer(layerId)) {
                source.isVisible = this.map.getLayoutProperty(layerId, 'visibility') !== 'visible';
                const newVisibility = source.isVisible ? 'visible' : 'none';
                this.map.setLayoutProperty(layerId, 'visibility', newVisibility);
            }
        });
    }

    protected async waitUntilMapReady(): Promise<void> {
        const map = this.map;
        if (!map) {
            return;
        }

        if (map.loaded() && map.isStyleLoaded() && map.areTilesLoaded() && !map.isMoving()) {
            return;
        }

        await new Promise<void>((resolve) => {
            map.once('idle', () => resolve());
        });
    }
}
